/**
 * Auth API module
 */

import { apiClient } from './client'

export interface LoginRequest {
  email: string
  password: string
}

export interface LoginResponse {
  token: string
  user: {
    id: string
    email: string
    name?: string
    firstName?: string
    lastName?: string
    phone?: string
    dateOfBirth?: string
    birthdate?: string
    role?: string
    systemAdmin?: boolean
    updateDetails?: boolean
  }
}

export interface LoginOtpRequest {
  email: string
  password: string
}

export interface LoginOtpResponse {
  email: string
  maskedEmail?: string
  loginId?: string
}

export interface LoginOtpVerifyRequest {
  email: string
  otp: string
  loginId?: string
}

export interface RegisterRequest {
  firstName: string
  lastName: string
  email: string
  dateOfBirth: string
  password: string
  confirmPassword: string
}

export interface RegisterResponse {
  email: string
  maskedEmail?: string
  registrationId?: string
}

export interface RegisterOtpVerifyRequest {
  email: string
  otp: string
  registrationId?: string
}

export interface RegisterOtpResendRequest {
  email: string
  registrationId?: string
}

export interface MasqueradeTarget {
  id: string
  email: string
  firstName?: string
  lastName?: string
  name?: string
}

export interface MasqueradeTargetsResponse {
  users: MasqueradeTarget[]
}

export interface MasqueradeRequest {
  password: string
  targetUserId: string
  targetEmail: string
}

export interface UserResponse {
  id: string
  email: string
  name: string
  role: string
  firstName?: string
  lastName?: string
  nickName?: string
  phone?: string
  bio?: string
  dateOfBirth?: string
  profileImage?: string
  systemAdmin?: boolean
  updateDetails?: boolean
}

export interface UpdateCurrentUserRequest {
  firstName: string
  lastName: string
  nickName?: string
  phone?: string
  email: string
  bio?: string
  dateOfBirth?: string
  profileImage?: string
  updateDetails?: boolean
}

export interface ProfileImageUploadRequest {
  filename: string
  contentType: string
}

export interface ProfileImageUploadResponse {
  upload: {
    uploadUrl: string
    publicUrl: string
    method?: 'PUT' | 'POST'
    headers?: Record<string, string>
  }
}

export interface PasswordResetRequest {
  email: string
}

export interface VerifyPasswordTokenResponse {
  valid: boolean
}

export interface ResetPasswordRequest {
  password: string
  confirmPassword: string
}

export interface ResetPasswordResponse {
  email: string
}

export class AuthAPI {
  async login(credentials: LoginRequest, signal?: AbortSignal): Promise<LoginResponse> {
    return apiClient.post('/v2/auth/login', credentials, undefined, signal)
  }

  async requestLoginOtp(
    request: LoginOtpRequest,
    signal?: AbortSignal
  ): Promise<LoginOtpResponse> {
    return apiClient.post('/v2/auth/login/otp/request', request, undefined, signal)
  }

  async verifyLoginOtp(
    request: LoginOtpVerifyRequest,
    signal?: AbortSignal
  ): Promise<LoginResponse> {
    return apiClient.post('/v2/auth/login/otp/verify', request, undefined, signal)
  }

  async register(
    request: RegisterRequest,
    signal?: AbortSignal
  ): Promise<RegisterResponse> {
    return apiClient.post('/v2/auth/register', request, undefined, signal)
  }

  async verifyRegistrationOtp(
    request: RegisterOtpVerifyRequest,
    signal?: AbortSignal
  ): Promise<LoginResponse> {
    return apiClient.post('/v2/auth/register/otp/verify', request, undefined, signal)
  }

  async resendRegistrationOtp(
    request: RegisterOtpResendRequest,
    signal?: AbortSignal
  ): Promise<RegisterResponse> {
    return apiClient.post('/v2/auth/register/otp/resend', request, undefined, signal)
  }

  async requestPasswordReset(
    request: PasswordResetRequest,
    signal?: AbortSignal
  ): Promise<void> {
    await apiClient.post('/v2/auth/password-reset/request', request, undefined, signal)
  }

  async verifyPasswordToken(
    token: string,
    signal?: AbortSignal
  ): Promise<VerifyPasswordTokenResponse> {
    return apiClient.get('/v2/auth/password-reset/verify', { token }, signal)
  }

  async requestNewPasswordToken(
    token: string,
    signal?: AbortSignal
  ): Promise<void> {
    await apiClient.post('/v2/auth/password-reset/resend', { token }, undefined, signal)
  }

  async resetPassword(
    token: string,
    request: ResetPasswordRequest,
    signal?: AbortSignal
  ): Promise<ResetPasswordResponse> {
    return apiClient.post(`/v2/auth/password-reset/${token}`, request, undefined, signal)
  }

  async getCurrentUser(signal?: AbortSignal): Promise<UserResponse> {
    return apiClient.get('/v2/auth/me', undefined, signal)
  }

  async updateCurrentUser(
    request: UpdateCurrentUserRequest,
    signal?: AbortSignal
  ): Promise<UserResponse> {
    return apiClient.patch('/v2/auth/me', request, undefined, signal)
  }

  async requestProfileImageUpload(
    request: ProfileImageUploadRequest,
    signal?: AbortSignal
  ): Promise<ProfileImageUploadResponse> {
    return apiClient.post('/v2/auth/me/profileImageUpload', request, undefined, signal)
  }

  async logout(signal?: AbortSignal, departingToken?: string): Promise<void> {
    if (departingToken) {
      // This explicit logout belongs to the captured departing session, even if
      // another account signs in while it is in flight. Never borrow its bearer.
      const base = String(import.meta.env.VITE_CSAPI_URL || '').replace(/\/$/, '')
      const response = await fetch(`${base}/v2/auth/logout`, { method: 'POST', signal,
        headers: { Authorization: `Bearer ${departingToken}`, 'Content-Type': 'application/json' },
        cache: 'no-store', credentials: 'omit',
      })
      if (!response.ok) throw new Error('Server logout could not be confirmed')
      return
    }
    await apiClient.post('/v2/auth/logout', undefined, undefined, signal)
  }

  async searchMasqueradeTargets(
    query: string,
    limit = 8,
    signal?: AbortSignal
  ): Promise<MasqueradeTargetsResponse> {
    return apiClient.get('/v2/auth/masqueradeTargets', { q: query, limit }, signal)
  }

  async startMasquerade(
    request: MasqueradeRequest,
    signal?: AbortSignal
  ): Promise<LoginResponse> {
    return apiClient.post('/v2/auth/masquerade', request, undefined, signal)
  }

  async refreshToken(signal?: AbortSignal): Promise<LoginResponse> {
    return apiClient.post('/v2/auth/refresh', undefined, undefined, signal)
  }
}

export const authAPI = new AuthAPI()
